import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  MessageSquare,
  X,
  Send,
  User,
  FileText,
  GitMerge,
  Filter,
} from 'lucide-react';
import clsx from 'clsx';
import type { ApprovalWorkflow } from '@/types';
import { approvalsApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Status Badge ------------------------------------------------------------

function ApprovalStatusBadge({ status }: { status: ApprovalWorkflow['status'] }) {
  const config: Record<string, { label: string; bg: string; text: string; icon: React.ElementType }> = {
    pending: { label: 'Pending', bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock },
    approved: { label: 'Approved', bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle },
    rejected: { label: 'Rejected', bg: 'bg-red-50', text: 'text-red-700', icon: XCircle },
    cancelled: { label: 'Cancelled', bg: 'bg-gray-100', text: 'text-gray-600', icon: XCircle },
  };
  const c = config[status] || config.pending;
  const StatusIcon = c.icon;
  return (
    <span className={clsx('badge gap-1', c.bg, c.text)}>
      <StatusIcon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

// --- Workflow Type Badge -----------------------------------------------------

function WorkflowTypeBadge({ type }: { type: string }) {
  const config: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
    consolidation_run: { bg: 'bg-blue-50', text: 'text-blue-700', icon: GitMerge },
    journal_entry: { bg: 'bg-purple-50', text: 'text-purple-700', icon: FileText },
    period_close: { bg: 'bg-teal-50', text: 'text-teal-700', icon: Clock },
  };
  const c = config[type] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: FileText };
  const TypeIcon = c.icon;
  return (
    <span className={clsx('badge gap-1', c.bg, c.text)}>
      <TypeIcon className="w-3 h-3" />
      {type.replace(/_/g, ' ')}
    </span>
  );
}

// --- Comment Modal -----------------------------------------------------------

interface CommentModalProps {
  approval: ApprovalWorkflow;
  action: 'approve' | 'reject';
  onClose: () => void;
  onSubmit: (id: string, action: 'approve' | 'reject', comment: string) => Promise<void>;
}

function CommentModal({ approval, action, onClose, onSubmit }: CommentModalProps) {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(approval.id, action, comment);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            {action === 'approve' ? 'Approve' : 'Reject'} Workflow
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <WorkflowTypeBadge type={approval.workflow_type} />
            <span className="text-sm text-gray-600">{approval.entity_type} #{approval.entity_id}</span>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Comments</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={`Add ${action === 'reject' ? 'reason for rejection' : 'approval comments'}...`}
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting || (action === 'reject' && !comment.trim())}
            className={clsx(
              'text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center',
              action === 'approve'
                ? 'bg-green-600 text-white hover:bg-green-700 disabled:bg-green-300'
                : 'bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300',
            )}
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {action === 'approve' ? 'Approve' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Tab Type ----------------------------------------------------------------

type ApprovalTab = 'pending' | 'my_requests' | 'all';

// --- Main Page ---------------------------------------------------------------

export default function ApprovalWorkflows() {
  const { addNotification } = useAppStore();
  const [activeTab, setActiveTab] = useState<ApprovalTab>('pending');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pendingApprovals, setPendingApprovals] = useState<ApprovalWorkflow[]>([]);
  const [myRequests, setMyRequests] = useState<ApprovalWorkflow[]>([]);
  const [allApprovals, setAllApprovals] = useState<ApprovalWorkflow[]>([]);

  // Filters for "All" tab
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Modal
  const [modalApproval, setModalApproval] = useState<ApprovalWorkflow | null>(null);
  const [modalAction, setModalAction] = useState<'approve' | 'reject'>('approve');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [pending, requests, all] = await Promise.all([
        approvalsApi.pending().catch(() => []),
        approvalsApi.myRequests().catch(() => []),
        approvalsApi.list().catch(() => []),
      ]);
      setPendingApprovals(pending);
      setMyRequests(requests);
      setAllApprovals(all);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load approvals');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAction = async (id: string, action: 'approve' | 'reject', comment: string) => {
    try {
      if (action === 'approve') {
        await approvalsApi.approve(id, comment);
      } else {
        await approvalsApi.reject(id, comment);
      }
      addNotification({
        type: 'success',
        title: `Workflow ${action === 'approve' ? 'Approved' : 'Rejected'}`,
        message: `The workflow has been ${action === 'approve' ? 'approved' : 'rejected'}.`,
      });
      await loadData();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Action Failed', message: apiErr.detail || 'Failed to process action.' });
    }
  };

  const filteredAll = allApprovals.filter((a) => {
    if (statusFilter && a.status !== statusFilter) return false;
    if (typeFilter && a.workflow_type !== typeFilter) return false;
    return true;
  });

  const uniqueTypes = [...new Set(allApprovals.map((a) => a.workflow_type))].sort();

  const tabs: { id: ApprovalTab; label: string; count: number }[] = [
    { id: 'pending', label: 'Pending My Review', count: pendingApprovals.length },
    { id: 'my_requests', label: 'My Requests', count: myRequests.length },
    { id: 'all', label: 'All Approvals', count: allApprovals.length },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-700" />
          </div>
          <div>
            <div className="skeleton w-64 h-7 mb-1" />
            <div className="skeleton w-80 h-4" />
          </div>
        </div>
        <div className="card p-6 animate-pulse">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="skeleton w-full h-16" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
          <CheckCircle className="w-5 h-5 text-green-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Approval Workflows</h1>
          <p className="text-sm text-gray-500 mt-0.5">Review and manage approval requests</p>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700',
            )}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={clsx(
                'px-1.5 py-0.5 text-[10px] font-bold rounded-full',
                activeTab === tab.id ? 'bg-primary-100 text-primary-700' : 'bg-gray-200 text-gray-600',
              )}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Pending My Review */}
      {activeTab === 'pending' && (
        <div className="space-y-3">
          {pendingApprovals.length === 0 ? (
            <div className="card py-12 text-center">
              <CheckCircle className="w-10 h-10 mx-auto text-green-300 mb-2" />
              <h3 className="text-base font-medium text-gray-900">All caught up</h3>
              <p className="text-sm text-gray-500 mt-1">No pending approvals to review.</p>
            </div>
          ) : (
            pendingApprovals.map((approval) => (
              <div key={approval.id} className="card p-5 hover:shadow-card-hover transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <WorkflowTypeBadge type={approval.workflow_type} />
                        <ApprovalStatusBadge status={approval.status} />
                      </div>
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">{approval.entity_type}</span> #{approval.entity_id}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {approval.requested_by}
                        </span>
                        <span>{new Date(approval.created_at).toLocaleDateString()}</span>
                      </div>
                      {approval.comments && (
                        <p className="mt-2 text-xs text-gray-500 italic flex items-start gap-1">
                          <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          {approval.comments}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => {
                        setModalApproval(approval);
                        setModalAction('reject');
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1 inline" />
                      Reject
                    </button>
                    <button
                      onClick={() => {
                        setModalApproval(approval);
                        setModalAction('approve');
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <CheckCircle className="w-3.5 h-3.5 mr-1 inline" />
                      Approve
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* My Requests */}
      {activeTab === 'my_requests' && (
        <div className="card overflow-hidden">
          {myRequests.length === 0 ? (
            <div className="py-12 text-center">
              <Send className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No requests submitted.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="table-header">Type</th>
                    <th className="table-header">Entity</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Reviewed By</th>
                    <th className="table-header">Submitted</th>
                    <th className="table-header">Resolved</th>
                  </tr>
                </thead>
                <tbody>
                  {myRequests.map((req) => (
                    <tr key={req.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                      <td className="table-cell"><WorkflowTypeBadge type={req.workflow_type} /></td>
                      <td className="table-cell text-sm text-gray-700">{req.entity_type} #{req.entity_id}</td>
                      <td className="table-cell"><ApprovalStatusBadge status={req.status} /></td>
                      <td className="table-cell text-sm text-gray-600">{req.approved_by || '\u2014'}</td>
                      <td className="table-cell text-xs text-gray-500">{new Date(req.created_at).toLocaleDateString()}</td>
                      <td className="table-cell text-xs text-gray-500">
                        {req.resolved_at ? new Date(req.resolved_at).toLocaleDateString() : '\u2014'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* All Approvals */}
      {activeTab === 'all' && (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-sm font-semibold text-gray-900">All Approvals</h2>
            <div className="flex items-center gap-2">
              <Filter className="w-3.5 h-3.5 text-gray-400" />
              <select
                className="select-field text-xs w-32"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                className="select-field text-xs w-40"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                {uniqueTypes.map((t) => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
          </div>
          {filteredAll.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No approvals found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="table-header">Type</th>
                    <th className="table-header">Entity</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Requested By</th>
                    <th className="table-header">Reviewed By</th>
                    <th className="table-header">Created</th>
                    <th className="table-header">Resolved</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAll.map((a) => (
                    <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                      <td className="table-cell"><WorkflowTypeBadge type={a.workflow_type} /></td>
                      <td className="table-cell text-sm text-gray-700">{a.entity_type} #{a.entity_id}</td>
                      <td className="table-cell"><ApprovalStatusBadge status={a.status} /></td>
                      <td className="table-cell text-sm text-gray-600">{a.requested_by}</td>
                      <td className="table-cell text-sm text-gray-600">{a.approved_by || '\u2014'}</td>
                      <td className="table-cell text-xs text-gray-500">{new Date(a.created_at).toLocaleDateString()}</td>
                      <td className="table-cell text-xs text-gray-500">
                        {a.resolved_at ? new Date(a.resolved_at).toLocaleDateString() : '\u2014'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Comment Modal */}
      {modalApproval && (
        <CommentModal
          approval={modalApproval}
          action={modalAction}
          onClose={() => setModalApproval(null)}
          onSubmit={handleAction}
        />
      )}
    </div>
  );
}
